import { getLocalizedRoutes } from '@seek/melways-sites';
import { ShoppingCartProvider } from '@seek/shopping-cart-provider';
import type { ComponentProps } from 'node_modules/@types/react';
import type { RouteObject } from 'react-router-dom';

import type { FlowType } from 'src/types/jobFlow';

import App, { type AppProps } from './App';
import { routes } from './routes/routes';
import { log } from './utils/logging';
import { FeatureToggleProviderV1 } from './v1/contexts/featureToggle';
import { MainLayout } from './v1/views/layouts/MainLayout';
import { DraftLoader } from './v2/components/DraftLoader';
import { HirerAccountAndUserDataLoader } from './v2/components/HirerAccountAndUserDataLoader';
import { FeatureToggleProviderV2 } from './v2/contexts/featureToggle';
import { HirerInfoProvider } from './v2/contexts/hirerInfo';

/**
 * Function to render all V1 specific providers, layouts and elements
 * @param appProps props coming from sku
 * @param element Element to render including the container
 * @returns
 */
const renderElementV1 = (appProps: AppProps, element: JSX.Element) => (
  <FeatureToggleProviderV1>
    <MainLayout site={appProps.site} language={appProps.language}>
      {element}
    </MainLayout>
  </FeatureToggleProviderV1>
);

/**
 * Function to render all V2 specific providers, layouts and elements
 * @param appProps props coming from sku
 * @param element Element to render including the container
 * @returns
 */
const renderElementV2 = (_appProps: AppProps, element: JSX.Element) => (
  <HirerAccountAndUserDataLoader>
    <DraftLoader>
      <FeatureToggleProviderV2>
        <HirerInfoProvider>{element}</HirerInfoProvider>
      </FeatureToggleProviderV2>
    </DraftLoader>
  </HirerAccountAndUserDataLoader>
);

type CartSupportedFlowType = Extract<FlowType, 'create' | 'edit'>;

const isCartSupportedFlowType = (
  flowType: FlowType,
): flowType is CartSupportedFlowType =>
  flowType === 'create' || flowType === 'edit';

/**
 * Function to render providers by flow type
 * @param flowType CAJA flow type, e.g. 'create' | 'edit' | 'refresh' | 'multilocation'
 * @param element Element to render including the container
 * @note TODO: Can refactor ShoppingCartProvider into container of each flow (e.g. CreateFlowContainer or CreateFlowContainerV2)
 * once "route.version === 1" is removed. Because route.version === 1 will create two instances of ShoppingCartProviders,
 * resulting in its internal state not being shared across v1 and v2.
 * @returns
 */
const renderElementByFlowType = (flowType: FlowType, element: JSX.Element) => {
  if (isCartSupportedFlowType(flowType)) {
    const mapper: Record<
      CartSupportedFlowType,
      ComponentProps<typeof ShoppingCartProvider>['createProductSetId']
    > = {
      create: 'CreateProductSetForAdPosting',
      edit: 'CreateProductSetForAdEditing',
    };

    return (
      <ShoppingCartProvider
        createProductSetId={mapper[flowType]}
        useMockCart={false}
        logger={log}
      >
        {element}
      </ShoppingCartProvider>
    );
  }

  return element;
};

export const createRoutes = (props: AppProps): RouteObject[] => {
  const localiedRoutes = getLocalizedRoutes(routes)(props.site).filter(Boolean);

  return [
    {
      element: <App {...props} />,
      children: localiedRoutes.map((route) => ({
        ...route,
        id: `${route.id}-${route.language}`,
        path: route.route,
        element: renderElementByFlowType(
          route.flowType,
          route.version === 1
            ? renderElementV1(props, route.element)
            : renderElementV2(props, route.element),
        ),
      })),
      id: 'root',
    },
  ];
};
